import { md5 } from "./md5";

// ── PayFast merchant configuration ──────────────────────────────────────
// PAYFAST_PASSPHRASE: set on your PayFast dashboard under Settings >
// Security Passphrase. If you have a passphrase configured there, it MUST
// be set here too, or every payment will fail with a signature mismatch.
// If you do NOT have a passphrase set on your PayFast account, leave this
// as an empty string and the signature will be generated without it.
//
// IMPORTANT — passphrase exposure: this is a static front-end site with
// no backend, so this value will be visible in the compiled JS bundle to
// anyone who inspects it. This is the same level of exposure as the
// merchant_id and merchant_key already in this file, and PayFast's own
// "Onsite" payment flow is designed to work this way for sites without a
// server component. That said, the passphrase is the one PayFast secret
// that increases security if kept private — if you later add a backend
// (even a small serverless function), move this signature generation
// there instead and remove the passphrase from this file entirely.
export const PAYFAST_MERCHANT_ID = "35936047";
export const PAYFAST_MERCHANT_KEY = "fkpiyainfhvzf";
export const PAYFAST_PASSPHRASE = "E00d-Nu7r/t/ionE0unda7ion"; // ← set this if your PayFast account has a passphrase configured

export const PAYFAST_RETURN_URL =
  "https://foodandnutritionfoundation.com/thank-you";
export const PAYFAST_CANCEL_URL = "https://foodandnutritionfoundation.com/donate";
export const PAYFAST_NOTIFY_URL =
  "https://foodandnutritionfoundation.com/api/payfast-notify";

const PAYFAST_PROCESS_URL = "https://www.payfast.co.za/eng/process";

/**
 * Generates the PayFast "sign" signature for a standard (non-API) payment
 * form submission, following PayFast's documented steps exactly:
 *   1. Concatenate name=value pairs IN THE ORDER THEY ARE ADDED (not
 *      alphabetical — that ordering only applies to the separate API).
 *   2. URL-encode each value, with spaces as '+' and encoding in upper case.
 *   3. Append the passphrase (if set) as the final pair.
 *   4. MD5 hash the resulting string, lowercase.
 *
 * @param {Object} data - ordered key/value pairs as they'll be submitted
 * @param {string} passphrase
 * @returns {string} lowercase MD5 signature
 */
function generateSignature(data, passphrase) {
  let pfOutput = "";
  Object.entries(data).forEach(([key, val]) => {
    if (val !== "" && val !== undefined && val !== null) {
      // PayFast wants uppercase percent-encoding and '+' for spaces —
      // encodeURIComponent already uppercases hex digits; we only need
      // to swap %20 for + to match PayFast's expected format.
      const encoded = encodeURIComponent(String(val).trim()).replace(/%20/g, "+");
      pfOutput += `${key}=${encoded}&`;
    }
  });

  // Remove trailing '&'
  let getString = pfOutput.slice(0, -1);

  if (passphrase) {
    getString += `&passphrase=${encodeURIComponent(passphrase.trim()).replace(/%20/g, "+")}`;
  }

  return md5(getString).toLowerCase();
}

/**
 * Builds the full set of PayFast form fields (in the correct order PayFast
 * expects for signature generation) for a single donation, computes the
 * signature, and redirects the browser to PayFast's hosted payment page.
 */
export function redirectToPayFast({ amount, cause, frequency, donor = {} }) {
  const itemName = cause?.payfastItem || "Donation — Food and Nutrition Foundation";
  const isMonthly = frequency === "monthly";
  const formattedAmount = parseFloat(amount).toFixed(2);
  const today = new Date().toISOString().split("T")[0];

  // Order matters for the signature — keep merchant details first, then
  // buyer details, then transaction details, matching PayFast's own
  // Quickstart guide ordering.
  const data = {
    merchant_id: PAYFAST_MERCHANT_ID,
    merchant_key: PAYFAST_MERCHANT_KEY,
    return_url: PAYFAST_RETURN_URL,
    cancel_url: PAYFAST_CANCEL_URL,
    notify_url: PAYFAST_NOTIFY_URL,
    ...(donor.name_first && { name_first: donor.name_first }),
    ...(donor.name_last && { name_last: donor.name_last }),
    ...(donor.email && { email_address: donor.email }),
    amount: formattedAmount,
    item_name: itemName,
    ...(isMonthly && {
      subscription_type: "1",
      billing_date: today,
      recurring_amount: formattedAmount,
      frequency: "3",
      cycles: "0",
    }),
  };

  const signature = generateSignature(data, PAYFAST_PASSPHRASE);

  const params = new URLSearchParams({ ...data, signature });
  window.location.href = `${PAYFAST_PROCESS_URL}?${params.toString()}`;
}
